import {
  type HostConflictFailureCounts,
  type HostFamilyParameterFeedback,
  type ParameterAdditionOptions,
  type ParameterAvailabilitySummary,
  type ParameterCompletionNotification,
} from './models';

export function notifyParameterAddition(
  options: ParameterAdditionOptions,
  notify: (message: string) => void,
  message: string,
): void {
  if (!options.suppressNotifications) {
    notify(message);
  }
}

export function buildScopedBindingCompletionNotification(summary: {
  requestedCount: number;
  addedOrUpdatedCount: number;
  existingCount: number;
  conflictingCount: number;
  failedCount: number;
}): ParameterCompletionNotification {
  const bindingLabel = summary.addedOrUpdatedCount === 1 ? 'binding was' : 'bindings were';

  if (summary.requestedCount === 0) {
    return {
      severity: 'warning',
      message: 'No scoped parameters were configured to add.',
    };
  }

  if (summary.conflictingCount > 0 || summary.failedCount > 0) {
    return {
      severity: 'warning',
      message:
        `${summary.addedOrUpdatedCount} scoped parameter ${bindingLabel} added or updated, ${summary.existingCount} already matched, ${summary.conflictingCount} conflicted, and ${summary.failedCount} failed. Check Revit messages for details.`,
    };
  }

  if (summary.addedOrUpdatedCount > 0) {
    return {
      severity: 'success',
      message: `${summary.addedOrUpdatedCount} scoped parameter ${bindingLabel} added or updated.${
        summary.existingCount > 0 ? ` The remaining ${summary.existingCount} already matched.` : ''
      }`,
    };
  }

  return {
    severity: 'info',
    message: 'All scoped parameters already matched the requested category and binding scope.',
  };
}

export function parseHostConflictFailureCounts(
  message: string | null | undefined,
): HostConflictFailureCounts {
  const text = message ?? '';
  const conflictMatch = text.match(/(\d+)\s+conflicts?/i);
  const failedMatch = text.match(/(\d+)\s+(?:failed|errors?)/i);

  return {
    conflictingCount: conflictMatch ? Number(conflictMatch[1]) : 0,
    failedCount: failedMatch ? Number(failedMatch[1]) : 0,
  };
}

export function summarizeHostFamilyParameterFeedback(
  message: string | null | undefined,
  error: string | null | undefined,
): HostFamilyParameterFeedback {
  const hostText = [message, error]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join(' ');
  const counts = parseHostConflictFailureCounts(hostText);
  const detail = error?.trim() || message?.trim() || undefined;

  return {
    ...counts,
    detail,
  };
}

export function isNoEditableObjectTypeFamilyTargetError(
  message: string | null | undefined,
): boolean {
  return /No editable loadable families matched target object type\(s\):/i.test(
    message ?? '',
  );
}

export function buildProjectParameterCompletionNotification(
  summary: ParameterAvailabilitySummary,
): ParameterCompletionNotification {
  if (summary.requestedCount === 0) {
    return {
      severity: 'warning',
      message: 'No parameters were configured to add.',
    };
  }

  if (summary.availableAfterCount === summary.requestedCount) {
    if (summary.newlyAvailableCount > 0) {
      return {
        severity: 'success',
        message: summary.newlyAvailableCount === summary.requestedCount
          ? `Added ${summary.newlyAvailableCount} project parameter${
            summary.newlyAvailableCount === 1 ? '' : 's'
          }. Quick Fill can use them now.`
          : `${summary.newlyAvailableCount} project parameter${
            summary.newlyAvailableCount === 1 ? '' : 's'
          } became available. The remaining ${summary.availableBeforeCount} already existed.`,
      };
    }

    return {
      severity: 'info',
      message: 'All configured parameters were already available in the project. No new parameters were added.',
    };
  }

  if (summary.newlyAvailableCount > 0) {
    return {
      severity: 'warning',
      message: `${summary.newlyAvailableCount} project parameter${
        summary.newlyAvailableCount === 1 ? '' : 's'
      } became available, but ${summary.missingCount} ${
        summary.missingCount === 1 ? 'is' : 'are'
      } still missing. Check the shared parameter file and the Revit messages.`,
    };
  }

  return {
    severity: 'warning',
    message:
      'No configured parameters became available after the request. Check the shared parameter file and the Revit messages.',
  };
}

export function buildProjectBindingCompletionNotification(summary: {
  requestedCount: number;
  addedOrUpdatedCount: number;
  existingCount: number;
  conflictingCount: number;
  failedCount: number;
}): ParameterCompletionNotification {
  const parameterLabel = summary.addedOrUpdatedCount === 1 ? 'parameter was' : 'parameters were';

  if (summary.requestedCount === 0) {
    return {
      severity: 'warning',
      message: 'No parameters were configured to add.',
    };
  }

  if (summary.conflictingCount > 0 || summary.failedCount > 0) {
    return {
      severity: 'warning',
      message:
        `${summary.addedOrUpdatedCount} project ${parameterLabel} added or updated, ${summary.existingCount} already matched, ${summary.conflictingCount} conflicted, and ${summary.failedCount} failed. Check Revit messages for details.`,
    };
  }

  if (summary.addedOrUpdatedCount > 0) {
    return {
      severity: 'success',
      message: `${summary.addedOrUpdatedCount} project ${parameterLabel} added or updated.${
        summary.existingCount > 0 ? ` The remaining ${summary.existingCount} already matched.` : ''
      }`,
    };
  }

  return {
    severity: 'info',
    message: 'All configured project parameters already matched the requested binding scope.',
  };
}
